import { timeout } from 'vscode/vscode/vs/base/common/async';
import { EditorExtensionsRegistry } from 'vscode/vscode/vs/editor/browser/editorExtensions';
import { EditorContextKeys } from 'vscode/vscode/vs/editor/common/editorContextKeys';
import { localizeWithPath } from 'vscode/vscode/vs/nls';
import { CONTEXT_ACCESSIBILITY_MODE_ENABLED } from 'vscode/vscode/vs/platform/accessibility/common/accessibility';
import { registerAction2, Action2 } from 'vscode/vscode/vs/platform/actions/common/actions';
import { Extensions } from 'vscode/vscode/vs/platform/configuration/common/configurationRegistry';
import { ContextKeyExpr } from 'vscode/vscode/vs/platform/contextkey/common/contextkey';
import { InputFocusedContextKey } from 'vscode/vscode/vs/platform/contextkey/common/contextkeys';
import { Registry } from 'vscode/vscode/vs/platform/registry/common/platform';
import { InlineChatController } from 'vscode/vscode/vs/workbench/contrib/inlineChat/browser/inlineChatController';
import { CTX_NOTEBOOK_CHAT_OUTER_FOCUS_POSITION } from 'vscode/vscode/vs/workbench/contrib/notebook/browser/controller/chat/notebookChatContext';
import { NotebookCellAction, NOTEBOOK_EDITOR_WIDGET_ACTION_WEIGHT, findTargetCellEditor, NotebookAction } from 'vscode/vscode/vs/workbench/contrib/notebook/browser/controller/coreActions';
import { CellEditState } from 'vscode/vscode/vs/workbench/contrib/notebook/browser/notebookBrowser';
import { NOTEBOOK_EDITOR_CURSOR_BOUNDARY, CellKind } from 'vscode/vscode/vs/workbench/contrib/notebook/common/notebookCommon';
import { NOTEBOOK_OUTPUT_INPUT_FOCUSED, NOTEBOOK_EDITOR_FOCUSED, NOTEBOOK_CELL_TYPE, NOTEBOOK_CELL_MARKDOWN_EDIT_MODE, NOTEBOOK_CURSOR_NAVIGATION_MODE, NOTEBOOK_OUTPUT_FOCUSED, NOTEBOOK_CELL_HAS_OUTPUTS } from 'vscode/vscode/vs/workbench/contrib/notebook/common/notebookContextKeys';

const _moduleId = "vs/workbench/contrib/notebook/browser/contrib/navigation/arrow";
const NOTEBOOK_FOCUS_TOP = 'notebook.focusTop';
const NOTEBOOK_FOCUS_BOTTOM = 'notebook.focusBottom';
const NOTEBOOK_FOCUS_PREVIOUS_EDITOR = 'notebook.focusPreviousEditor';
const NOTEBOOK_FOCUS_NEXT_EDITOR = 'notebook.focusNextEditor';
const FOCUS_IN_OUTPUT_COMMAND_ID = 'notebook.cell.focusInOutput';
const FOCUS_OUT_OUTPUT_COMMAND_ID = 'notebook.cell.focusOutOutput';
const CENTER_ACTIVE_CELL = 'notebook.centerActiveCell';
const NOTEBOOK_CURSOR_PAGEUP_COMMAND_ID = 'notebook.cell.cursorPageUp';
const NOTEBOOK_CURSOR_PAGEUP_SELECT_COMMAND_ID = 'notebook.cell.cursorPageUpSelect';
const NOTEBOOK_CURSOR_PAGEDOWN_COMMAND_ID = 'notebook.cell.cursorPageDown';
const NOTEBOOK_CURSOR_PAGEDOWN_SELECT_COMMAND_ID = 'notebook.cell.cursorPageDownSelect';
registerAction2(class extends Action2 {
    constructor() {
        super({
            id: 'notebook.cell.nullAction',
            title: ( localizeWithPath(
                _moduleId,
                0,
                "Keypresses that should be handled by the focused element in the cell output."
            )),
            keybinding: [{
                    when: NOTEBOOK_OUTPUT_INPUT_FOCUSED,
                    primary: 18 ,
                    weight: 200  + 1
                }, {
                    when: NOTEBOOK_OUTPUT_INPUT_FOCUSED,
                    primary: 16 ,
                    weight: 200  + 1
                }],
            f1: false
        });
    }
    run() {
        return;
    }
});
registerAction2(class FocusNextCellAction extends NotebookCellAction {
    constructor() {
        super({
            id: NOTEBOOK_FOCUS_NEXT_EDITOR,
            title: ( localizeWithPath(_moduleId, 1, 'Focus Next Cell Editor')),
            keybinding: [
                {
                    when: ( (ContextKeyExpr.and(
                        NOTEBOOK_EDITOR_FOCUSED,
                         (CONTEXT_ACCESSIBILITY_MODE_ENABLED.negate()),
                         (ContextKeyExpr.equals('config.notebook.navigation.allowNavigateToSurroundingCells', true)),
                         (ContextKeyExpr.and(
                             (ContextKeyExpr.has(InputFocusedContextKey)),
                            EditorContextKeys.editorTextFocus,
                             (NOTEBOOK_EDITOR_CURSOR_BOUNDARY.notEqualsTo('top')),
                             (NOTEBOOK_EDITOR_CURSOR_BOUNDARY.notEqualsTo('none'))
                        )),
                         (EditorContextKeys.isEmbeddedDiffEditor.negate())
                    ))),
                    primary: 18 ,
                    weight: NOTEBOOK_EDITOR_WIDGET_ACTION_WEIGHT,
                },
                {
                    when: ( (ContextKeyExpr.and(
                        NOTEBOOK_EDITOR_FOCUSED,
                         (CONTEXT_ACCESSIBILITY_MODE_ENABLED.negate()),
                         (ContextKeyExpr.equals('config.notebook.navigation.allowNavigateToSurroundingCells', true)),
                         (ContextKeyExpr.and(
                             (NOTEBOOK_CELL_TYPE.isEqualTo('markup')),
                             (NOTEBOOK_CELL_MARKDOWN_EDIT_MODE.isEqualTo(false)),
                            NOTEBOOK_CURSOR_NAVIGATION_MODE
                        )),
                         (EditorContextKeys.isEmbeddedDiffEditor.negate())
                    ))),
                    primary: 18 ,
                    weight: 200 ,
                },
                {
                    when: ( (ContextKeyExpr.and(NOTEBOOK_EDITOR_FOCUSED, NOTEBOOK_OUTPUT_FOCUSED))),
                    primary: 2048  | 18 ,
                    mac: { primary: 256  | 2048  | 18 , },
                    weight: 200
                },
                {
                    when: NOTEBOOK_EDITOR_FOCUSED,
                    primary: 2048  | 512  | 12 ,
                    mac: { primary: 2048  | 512  | 12 , },
                    weight: 200
                },
            ]
        });
    }
    async runWithContext(accessor, context) {
        const editor = context.notebookEditor;
        const activeCell = context.cell;
        const idx = editor.getCellIndex(activeCell);
        if (typeof idx !== 'number') {
            return;
        }
        if (idx >= editor.getLength() - 1) {
            return;
        }
        const focusEditorLine = activeCell.textBuffer.getLineCount();
        const targetCell = (context.cell ?? context.selectedCells?.[0]);
        const foundEditor = targetCell ? findTargetCellEditor(context, targetCell) : undefined;
        if (foundEditor && foundEditor.hasTextFocus() && InlineChatController.get(foundEditor)?.getWidgetPosition()?.lineNumber === focusEditorLine) {
            InlineChatController.get(foundEditor)?.focus();
        }
        else {
            const newCell = editor.cellAt(idx + 1);
            const newFocusMode = newCell.cellKind === CellKind.Markup && newCell.getEditState() === CellEditState.Preview ? 'container' : 'editor';
            await editor.focusNotebookCell(newCell, newFocusMode, { focusEditorLine: 1 });
        }
    }
});
registerAction2(class FocusPreviousCellAction extends NotebookCellAction {
    constructor() {
        super({
            id: NOTEBOOK_FOCUS_PREVIOUS_EDITOR,
            title: ( localizeWithPath(_moduleId, 2, 'Focus Previous Cell Editor')),
            keybinding: [
                {
                    when: ( (ContextKeyExpr.and(
                        NOTEBOOK_EDITOR_FOCUSED,
                         (CONTEXT_ACCESSIBILITY_MODE_ENABLED.negate()),
                         (ContextKeyExpr.equals('config.notebook.navigation.allowNavigateToSurroundingCells', true)),
                         (ContextKeyExpr.and(
                             (ContextKeyExpr.has(InputFocusedContextKey)),
                            EditorContextKeys.editorTextFocus,
                             (NOTEBOOK_EDITOR_CURSOR_BOUNDARY.notEqualsTo('bottom')),
                             (NOTEBOOK_EDITOR_CURSOR_BOUNDARY.notEqualsTo('none'))
                        )),
                         (EditorContextKeys.isEmbeddedDiffEditor.negate())
                    ))),
                    primary: 16 ,
                    weight: NOTEBOOK_EDITOR_WIDGET_ACTION_WEIGHT,
                },
                {
                    when: ( (ContextKeyExpr.and(
                        NOTEBOOK_EDITOR_FOCUSED,
                         (CONTEXT_ACCESSIBILITY_MODE_ENABLED.negate()),
                         (ContextKeyExpr.equals('config.notebook.navigation.allowNavigateToSurroundingCells', true)),
                         (ContextKeyExpr.and(
                             (NOTEBOOK_CELL_TYPE.isEqualTo('markup')),
                             (NOTEBOOK_CELL_MARKDOWN_EDIT_MODE.isEqualTo(false)),
                            NOTEBOOK_CURSOR_NAVIGATION_MODE
                        )),
                         (EditorContextKeys.isEmbeddedDiffEditor.negate())
                    ))),
                    primary: 16 ,
                    weight: 200 ,
                },
                {
                    when: NOTEBOOK_EDITOR_FOCUSED,
                    primary: 2048  | 512  | 11 ,
                    mac: { primary: 2048  | 512  | 11  },
                    weight: 200
                },
            ],
        });
    }
    async runWithContext(accessor, context) {
        const editor = context.notebookEditor;
        const activeCell = context.cell;
        const idx = editor.getCellIndex(activeCell);
        if (typeof idx !== 'number') {
            return;
        }
        if (idx < 1 || editor.getLength() === 0) {
            return;
        }
        const newCell = editor.cellAt(idx - 1);
        const newFocusMode = newCell.cellKind === CellKind.Markup && newCell.getEditState() === CellEditState.Preview ? 'container' : 'editor';
        const focusEditorLine = newCell.textBuffer.getLineCount();
        await editor.focusNotebookCell(newCell, newFocusMode, { focusEditorLine: focusEditorLine });
        const foundEditor = findTargetCellEditor(context, newCell);
        if (foundEditor && InlineChatController.get(foundEditor)?.getWidgetPosition()?.lineNumber === focusEditorLine) {
            InlineChatController.get(foundEditor)?.focus();
        }
    }
});
registerAction2(class extends NotebookAction {
    constructor() {
        super({
            id: NOTEBOOK_FOCUS_TOP,
            title: ( localizeWithPath(_moduleId, 3, 'Focus First Cell')),
            keybinding: [
                {
                    when: ( (ContextKeyExpr.and(NOTEBOOK_EDITOR_FOCUSED, ContextKeyExpr.not(InputFocusedContextKey)))),
                    primary: 2048  | 14 ,
                    weight: 200
                },
                {
                    when: ( (ContextKeyExpr.and(
                        NOTEBOOK_EDITOR_FOCUSED,
                        ContextKeyExpr.not(InputFocusedContextKey),
                         (CTX_NOTEBOOK_CHAT_OUTER_FOCUS_POSITION.isEqualTo(''))
                    ))),
                    mac: { primary: 2048  | 16  },
                    weight: 200
                }
            ],
        });
    }
    async runWithContext(accessor, context) {
        const editor = context.notebookEditor;
        if (editor.getLength() === 0) {
            return;
        }
        const firstCell = editor.cellAt(0);
        await editor.focusNotebookCell(firstCell, 'container');
    }
});
registerAction2(class extends NotebookAction {
    constructor() {
        super({
            id: NOTEBOOK_FOCUS_BOTTOM,
            title: ( localizeWithPath(_moduleId, 4, 'Focus Last Cell')),
            keybinding: [
                {
                    when: ( (ContextKeyExpr.and(NOTEBOOK_EDITOR_FOCUSED, ContextKeyExpr.not(InputFocusedContextKey)))),
                    primary: 2048  | 13 ,
                    mac: undefined,
                    weight: 200
                },
                {
                    when: ( (ContextKeyExpr.and(
                        NOTEBOOK_EDITOR_FOCUSED,
                        ContextKeyExpr.not(InputFocusedContextKey),
                         (CTX_NOTEBOOK_CHAT_OUTER_FOCUS_POSITION.isEqualTo(''))
                    ))),
                    mac: { primary: 2048  | 18  },
                    weight: 200
                }
            ],
        });
    }
    async runWithContext(accessor, context) {
        const editor = context.notebookEditor;
        if (!editor.hasModel() || editor.getLength() === 0) {
            return;
        }
        const lastIdx = editor.getLength() - 1;
        const lastVisibleIdx = editor.getPreviousVisibleCellIndex(lastIdx);
        if (lastVisibleIdx) {
            const cell = editor.cellAt(lastVisibleIdx);
            await editor.focusNotebookCell(cell, 'container');
        }
    }
});
registerAction2(class extends NotebookCellAction {
    constructor() {
        super({
            id: FOCUS_IN_OUTPUT_COMMAND_ID,
            title: ( localizeWithPath(_moduleId, 5, 'Focus In Active Cell Output')),
            keybinding: {
                when: ( (ContextKeyExpr.and(NOTEBOOK_EDITOR_FOCUSED, NOTEBOOK_CELL_HAS_OUTPUTS))),
                primary: 2048  | 18 ,
                mac: { primary: 256  | 2048  | 18 , },
                weight: 200
            },
        });
    }
    async runWithContext(accessor, context) {
        const editor = context.notebookEditor;
        const activeCell = context.cell;
        return timeout(0).then(() => editor.focusNotebookCell(activeCell, 'output'));
    }
});
registerAction2(class extends NotebookCellAction {
    constructor() {
        super({
            id: FOCUS_OUT_OUTPUT_COMMAND_ID,
            title: ( localizeWithPath(_moduleId, 6, 'Focus Out Active Cell Output')),
            keybinding: {
                when: ( (ContextKeyExpr.and(NOTEBOOK_EDITOR_FOCUSED, NOTEBOOK_OUTPUT_FOCUSED))),
                primary: 2048  | 16 ,
                mac: { primary: 256  | 2048  | 16 , },
                weight: 200
            },
        });
    }
    async runWithContext(accessor, context) {
        const editor = context.notebookEditor;
        const activeCell = context.cell;
        await editor.focusNotebookCell(activeCell, 'editor');
    }
});
registerAction2(class CenterActiveCellAction extends NotebookCellAction {
    constructor() {
        super({
            id: CENTER_ACTIVE_CELL,
            title: ( localizeWithPath(_moduleId, 7, "Center Active Cell")),
            keybinding: {
                when: NOTEBOOK_EDITOR_FOCUSED,
                primary: 2048  | 42 ,
                mac: {
                    primary: 256  | 42 ,
                },
                weight: 200
            },
        });
    }
    async runWithContext(accessor, context) {
        return context.notebookEditor.revealInCenter(context.cell);
    }
});
registerAction2(class extends NotebookCellAction {
    constructor() {
        super({
            id: NOTEBOOK_CURSOR_PAGEUP_COMMAND_ID,
            title: ( localizeWithPath(_moduleId, 8, "Cell Cursor Page Up")),
            keybinding: [
                {
                    when: ( (ContextKeyExpr.and(
                        NOTEBOOK_EDITOR_FOCUSED,
                         (ContextKeyExpr.has(InputFocusedContextKey)),
                        EditorContextKeys.editorTextFocus
                    ))),
                    primary: 11 ,
                    weight: NOTEBOOK_EDITOR_WIDGET_ACTION_WEIGHT
                }
            ]
        });
    }
    async runWithContext(accessor, context) {
        EditorExtensionsRegistry.getEditorCommand('cursorPageUp').runCommand(accessor, { pageSize: getPageSize(context) });
    }
});
registerAction2(class extends NotebookCellAction {
    constructor() {
        super({
            id: NOTEBOOK_CURSOR_PAGEUP_SELECT_COMMAND_ID,
            title: ( localizeWithPath(_moduleId, 9, "Cell Cursor Page Up Select")),
            keybinding: [
                {
                    when: ( (ContextKeyExpr.and(
                        NOTEBOOK_EDITOR_FOCUSED,
                         (ContextKeyExpr.has(InputFocusedContextKey)),
                        EditorContextKeys.editorTextFocus,
                         (NOTEBOOK_OUTPUT_FOCUSED.negate())
                    ))),
                    primary: 1024  | 11 ,
                    weight: NOTEBOOK_EDITOR_WIDGET_ACTION_WEIGHT
                }
            ]
        });
    }
    async runWithContext(accessor, context) {
        EditorExtensionsRegistry.getEditorCommand('cursorPageUpSelect').runCommand(accessor, { pageSize: getPageSize(context) });
    }
});
registerAction2(class extends NotebookCellAction {
    constructor() {
        super({
            id: NOTEBOOK_CURSOR_PAGEDOWN_COMMAND_ID,
            title: ( localizeWithPath(_moduleId, 10, "Cell Cursor Page Down")),
            keybinding: [
                {
                    when: ( (ContextKeyExpr.and(
                        NOTEBOOK_EDITOR_FOCUSED,
                         (ContextKeyExpr.has(InputFocusedContextKey)),
                        EditorContextKeys.editorTextFocus
                    ))),
                    primary: 12 ,
                    weight: NOTEBOOK_EDITOR_WIDGET_ACTION_WEIGHT
                }
            ]
        });
    }
    async runWithContext(accessor, context) {
        EditorExtensionsRegistry.getEditorCommand('cursorPageDown').runCommand(accessor, { pageSize: getPageSize(context) });
    }
});
registerAction2(class extends NotebookCellAction {
    constructor() {
        super({
            id: NOTEBOOK_CURSOR_PAGEDOWN_SELECT_COMMAND_ID,
            title: ( localizeWithPath(_moduleId, 11, "Cell Cursor Page Down Select")),
            keybinding: [
                {
                    when: ( (ContextKeyExpr.and(
                        NOTEBOOK_EDITOR_FOCUSED,
                         (ContextKeyExpr.has(InputFocusedContextKey)),
                        EditorContextKeys.editorTextFocus,
                         (NOTEBOOK_OUTPUT_FOCUSED.negate())
                    ))),
                    primary: 1024  | 12 ,
                    weight: NOTEBOOK_EDITOR_WIDGET_ACTION_WEIGHT
                }
            ]
        });
    }
    async runWithContext(accessor, context) {
        EditorExtensionsRegistry.getEditorCommand('cursorPageDownSelect').runCommand(accessor, { pageSize: getPageSize(context) });
    }
});
function getPageSize(context) {
    const editor = context.notebookEditor;
    const layoutInfo = editor.getViewModel().layoutInfo;
    const lineHeight = layoutInfo?.fontInfo.lineHeight || 17;
    return Math.max(1, Math.floor((layoutInfo?.height || 0) / lineHeight) - 2);
}
( (Registry.as(Extensions.Configuration))).registerConfiguration({
    id: 'notebook',
    order: 100,
    type: 'object',
    'properties': {
        'notebook.navigation.allowNavigateToSurroundingCells': {
            type: 'boolean',
            default: true,
            markdownDescription: ( localizeWithPath(
                _moduleId,
                12,
                "When enabled cursor can navigate to the next/previous cell when the current cursor in the cell editor is at the first/last line."
            ))
        }
    }
});

export { CENTER_ACTIVE_CELL };
